import { createRouter, createWebHistory } from "vue-router";
import AppHome from "@/pages/Home.vue";
import ProductDetails from "@/pages/ProductDetails.vue";
import ProductList from "@/pages/ProductList.vue";
import ProductForm from "@/components/admin/ProductForm.vue";
import LoginForm from "@/pages/LoginForm.vue";
import RegisterForm from "@/pages/RegisterForm.vue";
import AdminProductList from "@/pages/admin/AdminProductList.vue";
import AdminOrderList from "@/pages/admin/AdminOrderList.vue";
import AdminOrderDetails from "@/pages/admin/AdminOrderDetails.vue";
import HowToGerminate from "@/pages/HowToGerminate.vue";
import AccountSettings from "@/pages/AccountSettings.vue";
import { logout, hasAdminPermission } from '@/services/auth';
import { canViewGallery } from '@/services/galleryVisibility';
import WarrantyForm from '@/pages/WarrantyForm.vue';
import UserList from '@/pages/admin/UserList.vue';
import UserDetail from '@/pages/admin/UserDetail.vue';
import TrophiesView from '@/pages/TrophiesView.vue';
import PanelLayout from '@/components/PanelLayout.vue';
import DistributorShop from '@/pages/DistributorShop.vue';
import PanelOrders from '@/pages/panel/PanelOrders.vue';
import PanelBanners from '@/pages/panel/PanelBanners.vue';
import PanelMarketing from '@/pages/panel/PanelMarketing.vue';
import PanelInvitations from '@/pages/panel/PanelInvitations.vue';
import PanelStoreSettings from '@/pages/panel/PanelStoreSettings.vue';
import PanelStatistics from '@/pages/panel/PanelStatistics.vue';
import OrderResult from '@/pages/OrderResult.vue';
import { canAccessWholesale, canAccessIngresoShop } from '@/utils/b2bAccess';
import { isIngresoHost, ingresoDefaultRoute, mainSiteUrlForAdminOnIngreso } from '@/utils/host';
import { applyAuthHandoff } from '@/utils/authHandoff';

const routes = [
  // ── Rutas públicas ───────────────────────────────────────────────
  { path: "/",               name: "Home",          component: AppHome },
  { path: "/trofeos",        name: "TrophiesView",  component: TrophiesView },
  {
    path: "/galeria",
    name: "GalleryPage",
    component: () => import('@/pages/GalleryPage.vue'),
    // La galería se puede desactivar desde el panel (/panel/galeria-inicio).
    // Si está desactivada, los usuarios comunes no entran: van a la home. Los
    // administradores sí pueden, porque el backend les sigue devolviendo el
    // contenido para previsualizarla.
    beforeEnter: async () => {
      const allowed = await canViewGallery();
      return allowed ? true : { path: '/' };
    },
  },
  { path: "/como-germinar/", name: "HowToGerminate",component: HowToGerminate },
  { path: "/producto/:id",   name: "ProductDetails",component: ProductDetails },
  { path: "/productos/",     name: "ProductList",   component: ProductList },
  { path: "/merch/",         name: "MerchList",     component: ProductList, props: { isMerchPage: true } },
  { path: "/garantia",       name: "Warranty",      component: WarrantyForm },
  { path: "/blog",          name: "Blog",          component: () => import('@/pages/BlogListPage.vue') },
  { path: "/blog/:slug",    name: "BlogPost",      component: () => import('@/pages/BlogPost.vue'), props: true },
  { path: "/pedido/:orderId", name: "OrderResult", component: OrderResult, meta: { requiresAuth: true } },

  // ── Panel ingreso (misma estructura que admin, menos ítems) ──
  {
    path: '/ingreso',
    component: PanelLayout,
    meta: { requiresAuth: true, requiresIngresoAccess: true, panelVariant: 'ingreso' },
    redirect: '/panel/pedido-rapido',
    children: [
      {
        path: 'tienda',
        redirect: '/panel/pedido-rapido',
      },
      {
        path: 'pedidos',
        name: 'IngresoOrders',
        component: () => import('@/pages/ingreso/IngresoOrders.vue'),
      },
      {
        path: 'pedidos/:orderId',
        name: 'IngresoOrderDetail',
        component: () => import('@/pages/panel/PanelOrderDetail.vue'),
      },
      {
        path: 'cuenta',
        name: 'IngresoAccount',
        component: () => import('@/pages/ingreso/IngresoAccount.vue'),
      },
    ],
  },

  // ── Auth ──────────────────────────────────────────────────────────
  {
    path: "/activar-cuenta/:token",
    name: "ActivateAccount",
    component: () => import('@/pages/ActivateAccount.vue'),
    meta: { redirectIfAuth: false },
  },
  { path: "/iniciar-sesion", redirect: "/login" },
  {
    path: "/login",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/registro",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true }
  },

  // ── Panel unificado (/panel/...) ──────────────────────────────────
  // Sidebar flotante + contenido a la derecha. La navegación se adapta
  // según el rol (cliente comprador vs. administrador).
  {
    path: "/panel",
    component: PanelLayout,
    meta: { requiresAuth: true },
    children: [
      // Redirección por rol al entrar a /panel
      {
        path: "",
        redirect: () => {
          if (localStorage.getItem('isAdmin') === 'true') return '/panel/productos'
          return '/panel/pedido-rapido'
        },
      },

      // ── Secciones comunes / cliente ──
      { path: "cuenta",        name: "PanelAccount",   component: AccountSettings },
      { path: "mis-pedidos",   name: "PanelOrders",    component: PanelOrders },
      {
        path: "mis-pedidos/:orderId",
        name: "PanelOrderDetail",
        component: () => import('@/pages/panel/PanelOrderDetail.vue'),
      },
      { path: "pedido-rapido", name: "DistributorShop", component: DistributorShop },
      { path: "catalogo", redirect: { name: "DistributorShop" } },

      // ── Secciones de administración ──
      { path: "estadisticas", name: "PanelStatistics", component: PanelStatistics, meta: { requiresAdmin: true } },
      { path: "productos",  name: "AdminProductList",   component: AdminProductList,    meta: { requiresAdmin: true } },
      { path: "productos/nuevo",             name: "ProductForm", component: ProductForm, meta: { requiresAdmin: true } },
      { path: "productos/editar/:productId", name: "ProductEdit", component: ProductForm, meta: { requiresAdmin: true } },
      { path: "pedidos",            name: "AdminOrderList",    component: AdminOrderList,    meta: { requiresAdmin: true } },
      { path: "pedidos/:orderId",   name: "AdminOrderDetails", component: AdminOrderDetails, meta: { requiresAdmin: true } },
      { path: "banners",    name: "PanelBanners",        component: PanelBanners,        meta: { requiresAdmin: true } },
      { path: "galeria-inicio", name: "HomeGalleryManager", component: () => import('@/pages/admin/HomeGalleryManager.vue'), meta: { requiresAdmin: true } },
      { path: "trofeos",    name: "TrophiesManager",     component: () => import('@/pages/admin/TrophiesManager.vue'), meta: { requiresAdmin: true } },
      { path: "blog",            name: "BlogList",       component: () => import('@/pages/admin/BlogList.vue'), meta: { requiresAdmin: true } },
      { path: "blog/nuevo",      name: "BlogNew",        component: () => import('@/pages/admin/BlogEdit.vue'), meta: { requiresAdmin: true } },
      { path: "blog/:id",        name: "BlogEdit",       component: () => import('@/pages/admin/BlogEdit.vue'), meta: { requiresAdmin: true } },
      { path: "promociones", name: "PanelMarketing",     component: PanelMarketing,      meta: { requiresAdmin: true } },
      { path: "invitaciones", name: "PanelInvitations", component: PanelInvitations,    meta: { requiresAdmin: true } },
      { path: "roles", name: "PanelRoles", component: () => import('@/pages/panel/PanelRoles.vue'), meta: { requiresAdmin: true } },
      {
        path: "ingreso-catalog",
        redirect: { name: "AdminProductList", query: { tab: "tienda" } },
      },
      {
        path: "envios",
        name: "PanelShippingOrders",
        component: () => import('@/pages/panel/PanelShippingOrders.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: "envios/configuracion",
        name: "PanelShippingSettings",
        component: () => import('@/pages/panel/PanelShippingSettings.vue'),
        meta: { requiresAdmin: true },
      },
      { path: "configuracion", name: "PanelStoreSettings", component: PanelStoreSettings, meta: { requiresAdmin: true } },
      { path: "usuarios",   name: "UserList",            component: UserList,            meta: { requiresAdmin: true } },
      { path: "usuarios/:id", name: "UserDetail",        component: UserDetail,          meta: { requiresAdmin: true } },
    ]
  },

  // ── Redirecciones legacy ──────────────────────────────────────────
  { path: "/carrito", redirect: "/panel/pedido-rapido" },
  { path: "/carrito/", redirect: "/panel/pedido-rapido" },
  { path: "/mi-cuenta",         redirect: "/panel/cuenta" },
  { path: "/pedido-rapido", redirect: "/panel/pedido-rapido" },
  { path: "/catalogo", redirect: "/panel/pedido-rapido" },
  { path: "/admin",             redirect: "/panel" },
  { path: "/admin/dashboard",   redirect: "/panel/estadisticas" },
  { path: "/admin/productos",   redirect: "/panel/productos" },
  { path: "/panel/ingreso-catalog", redirect: { name: "AdminProductList", query: { tab: "tienda" } } },
  { path: "/admin/pedidos",     redirect: "/panel/pedidos" },
  { path: "/admin/actividad",   redirect: "/panel/productos" },
  { path: "/admin/bancos",      redirect: "/panel/productos" },
  { path: "/panel/bancos",      redirect: "/panel/productos" },
  { path: "/panel/bancos/:pathMatch(.*)*", redirect: "/panel/productos" },
  { path: "/admin/garantias",   redirect: "/panel/productos" },
  { path: "/admin/marcas",      redirect: "/panel/productos" },
  { path: "/admin/usuarios",    redirect: "/panel/usuarios" },
  { path: "/panel/dashboard",   redirect: "/panel/estadisticas" },
  { path: "/panel/actividad",   redirect: "/panel/productos" },
  { path: "/panel/garantias",   redirect: "/panel/productos" },
  { path: "/panel/marcas",      redirect: "/panel/productos" },
  { path: "/panel/garantia",    redirect: "/garantia" },
  { path: "/admin/:pathMatch(.*)*", redirect: to => `/panel/${to.params.pathMatch}` },

  // Catch-all
  { path: "/:pathMatch(.*)*", redirect: "/" }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  applyAuthHandoff();

  const isAuthenticated = !!localStorage.getItem('accessToken');
  const matched = to.matched;
  const requiresAuth = matched.some((r) => r.meta.requiresAuth);
  const requiresAdmin = matched.some((r) => r.meta.requiresAdmin);
  const requiresWholesale = matched.some((r) => r.meta.requiresWholesale);
  const requiresIngresoAccess = matched.some((r) => r.meta.requiresIngresoAccess);
  const redirectIfAuth = matched.some((r) => r.meta.redirectIfAuth);

  if (isAuthenticated && isIngresoHost()) {
    const adminPanelPath = to.path.startsWith('/panel') ? to.fullPath : '/panel';
    const adminMainUrl = mainSiteUrlForAdminOnIngreso(adminPanelPath);
    if (adminMainUrl && to.name !== 'ActivateAccount') {
      window.location.replace(adminMainUrl);
      return;
    }
  }

  if (isIngresoHost() && to.path === '/') {
    return next(isAuthenticated ? '/panel/pedido-rapido' : '/login');
  }

  if (to.name === 'ActivateAccount') {
    return next();
  }

  if (redirectIfAuth && isAuthenticated) {
    const adminMainUrl = mainSiteUrlForAdminOnIngreso('/panel');
    if (adminMainUrl) {
      window.location.replace(adminMainUrl);
      return;
    }
    return next(isIngresoHost() ? ingresoDefaultRoute() : '/panel');
  }

  if (requiresAuth || to.path.startsWith('/ingreso')) {
    if (!isAuthenticated) {
      localStorage.setItem('intendedRoute', to.fullPath);
      return next({ name: 'LoginForm', query: { redirect: to.fullPath } });
    }

    if (requiresAdmin) {
      try {
        const hasAdmin = await hasAdminPermission();
        if (!hasAdmin) {
          console.warn('Acceso denegado: sin permisos de administrador');
          return next('/panel');
        }
      } catch (error) {
        console.error('Error verificando permisos de admin:', error);
        if (error.response?.status === 401) {
          logout();
          return next({ name: 'LoginForm', query: { redirect: to.fullPath } });
        }
        return next('/panel');
      }
    }

    if (requiresWholesale && !canAccessWholesale()) {
      return next('/panel/cuenta');
    }

    if (requiresIngresoAccess && !canAccessIngresoShop()) {
      return next({ path: '/panel/cuenta', query: { pending_approval: '1' } });
    }
  }

  next();
});

export default router;
